import React, { Component } from 'react';
import { Button, Card } from 'react-bootstrap';
import { GoX } from 'react-icons/go';
import { LuArrowRight } from 'react-icons/lu';
import Tooltip from '../../../../../controls/common/tooltip';
import StatusPill from '../../components/statusPill';
import MetaThread from './metaThread';
import CellActions from './cellActions';
import CellSections from './cellSections';

/**
 * CellPanel — right-docked detail panel for a selected cell.
 *
 * Layout: one "Cell" section (`Card.Text as="h6"` + `<hr/>`) with a single-row Status properties table,
 *   then the three collapsible subsections (CellSections) — Details (title + description), Cell Workflow
 *   (cell_workflow_mode + the specify-mode workflow / mini-graph), and Workspace (workspace_mode + the
 *   specify-mode workspace) — followed by the Actions section and the Meta-thread timeline. Structural pickers are editable while
 *   isStructureEditable(cell) is true; once the cell has run they are replaced by the read-only view of
 *   what it froze (see cellRules.js, and cellWorkflowView.js for the shared frozen rendering).
 *
 * Cell Workflow and Workspace each carry a MODE (cell_workflow_mode / workspace_mode) with the same
 *   choices — inherit_from_mission · specify_* · inherit_from_previous_cell. Only the "specify" mode shows
 *   a second Select (a workflow / workspace to pick); both inherit modes are resolved at RUNTIME by the
 *   orchestrator, so picking one writes the mode with the value unresolved (null) and shows no second
 *   picker. New cells default both modes to inherit_from_mission.
 *
 * Actions — the applicable actions for the cell's status, one compact card each; hidden when none apply:
 *   draft → Activate · input_needed → Answer Input (or Approve / Request Changes for a review gate) ·
 *   failed → Retry · ready|working → Pause · inactive → Resume.
 *
 * Details (title + description) are editable only while status === "draft", read-only otherwise.
 *
 * `canEdit` (default true) is the OTHER reason everything here can be read-only: a committed
 * revision, a closed session, or no write access. It folds into the same two gates rather than
 * adding a third, so a read-only viewer sees exactly what somebody looking at a cell that has
 * already run sees — the frozen views — and no control that cannot be pressed.
 *
 * Every write here is INCREMENTAL: it calls the client then applies a pure store reducer via
 * onApplyLocal — never a full reload. The panel stays open and the mission graph updates in place.
 * cell_workflow / workspace / workspace_mode freeze the first time the cell reaches "working".
 *
 * Props:
 *   cell         — cell vertex object from the store
 *   missionId    — string
 *   client       — SystemOfWorkClient instance
 *   onClose      — () => void
 *   onApplyLocal — (updater: store => store) => void — incremental local store update
 *   templates    — store.templates ({ workflows, workspaces } palettes)
 */

// Leading "clear" option for the workspace / cell-workflow name pickers (value '' → save clears to null).


// ---------------------------------------------------------------------------
// CellPanel — main panel component
// ---------------------------------------------------------------------------

class CellPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      metaItems: [],
      // Whether the preview's ten are a tail rather than the whole thread — see `previewMetaThread`.
      metaMore: false,
      metaLoading: false,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this._loadMetaThread();
  }

  componentDidUpdate(prevProps) {
    // Re-fetch meta-thread and re-seed editable drafts only when the selected cell's identity changes
    if ((prevProps.cell && prevProps.cell.element_id) !== (this.props.cell && this.props.cell.element_id)) {
      this._loadMetaThread();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async _loadMetaThread() {
    const { cell, client, missionId } = this.props;
    if (!cell || !client) return;

    this.setState({ metaLoading: true });
    try {
      const { items, more } = await client.previewMetaThread(missionId, cell.element_id);
      if (!this._mounted) return;
      this.setState({ metaItems: items || [], metaMore: !!more, metaLoading: false });
    } catch (_) {
      if (!this._mounted) return;
      this.setState({ metaItems: [], metaMore: false, metaLoading: false });
    }
  }

  // Shared write path for the workspace_mode + workspace pair (they always travel together — a mode
  // change without its resolved workspace, or vice versa, would leave the two inconsistent server-side).
  // Mirrors _saveCellWorkflow's write-then-reflect-locally shape.
  render() {
    const { cell, missionId, client, onClose, onOpen, templates, onApplyLocal } = this.props;
    const { metaItems, metaLoading, metaMore } = this.state;

    if (!cell) return null;

    const props = cell.props || {};
    const status = props.status || 'draft';

    // Two independent reasons this panel can be read-only, and they meet here rather than being
    // checked twice: the CELL froze its structure the first time it reached "working", or the
    // REVISION/SESSION is read-only for everyone. Folding the second into the existing gate means
    // read-only reuses the rendering a run-away cell already has — the frozen views — instead of a
    // second dialect of "you cannot change this".
    const canEdit = this.props.canEdit !== false;

    return (
      <>
        {(onClose || onOpen) && (
          <span className="workflow-controls-pane-right">
            {onOpen && (
              // An ARROW, because this changes screens. It was the fullscreen glyph, which names a
              // change of presentation rather than of place — and the way back was an ✕, the glyph
              // that closes a panel one click away in this same header. One act, one glyph: `→` and
              // `←` navigate (the cell's own screen carries the `←`), `✕` dismisses, everywhere.
              //
              // Rendered exactly like the close control beside it: a filled text button next to a
              // bare icon read as two different kinds of thing.
              <Tooltip text="Open cell">
                <Button variant="light" className="mr-1" onClick={onOpen} aria-label="Open cell">
                  <LuArrowRight />
                </Button>
              </Tooltip>
            )}
            {onClose && (
              <Button variant="light" onClick={onClose} aria-label="Close panel"><GoX /></Button>
            )}
          </span>
        )}

        {/* Static panel header — the cell's own title lives in the Details section below */}
        <Card.Text as="h6">Cell</Card.Text>
        <hr />

        {/* Cell ID + Status — the standard gray-chip properties table (value cells carry the .prop-value-column
            background). */}
        <Card.Text>
          <table className="properties">
            <tbody>
              <tr>
                <td className="prop-name-column">Cell ID</td>
                <td className="prop-value-column"><code>{cell.element_id}</code></td>
              </tr>
              <tr>
                <td className="prop-name-column">Status</td>
                <td className="prop-value-column"><StatusPill status={status} /></td>
              </tr>
            </tbody>
          </table>
        </Card.Text>

        {/* Details, Cell Workflow and Workspace — the three collapsible subsections, and which of
            them stand open. Rendered by the same component the cell's own screen renders, because
            they are the same three sections in the same order: writing them out twice is how the
            two surfaces came to differ before, the screen having no Workspace section at all. */}
        <CellSections cell={cell} missionId={missionId} client={client}
          templates={templates} onApplyLocal={onApplyLocal} canEdit={canEdit} />

        {/* Actions — one compact card per applicable action; the whole section hides when none apply */}
        <CellActions
          cell={cell}
          canEdit={canEdit}
          missionId={missionId}
          client={client}
          onActionDone={() => this._loadMetaThread()}
          onApplyLocal={onApplyLocal}
        />

        {/* `mt-3` for the reason the Actions heading above carries it: a Bootstrap heading has no
            top margin, so this sat directly on whatever the section before it ended with — the
            Workspace line, which owns no trailing space of its own. These two are the only headings
            in the widget that follow content; the other four open their card. */}
        <Card.Text as="h6" className="mt-3">Meta Thread</Card.Text>
        <hr />
        <MetaThread items={metaItems} loading={metaLoading} more={metaMore} />
      </>
    );
  }
}

export default CellPanel;
